import { supabase } from "@/lib/supabase";
import { recordActivity } from "@/services/activityLog";

/**
 * Appointments service. Booking goes through the `set_appointment` RPC
 * (migration 0039) so the stop outcome + lead status + visit event + the
 * appointment row land atomically. Status changes (done / cancelled / missed)
 * are plain RLS-scoped updates.
 */

export type AppointmentStatus = "scheduled" | "completed" | "cancelled" | "missed";

export interface AppointmentRow {
  id: string;
  lead_id: string | null;
  route_stop_id: string | null;
  assigned_to: string | null;
  scheduled_at: string;
  status: AppointmentStatus;
  notes: string | null;
  created_at: string;
  lead: {
    id: string;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    address_street: string | null;
    address_city: string | null;
    address_state: string | null;
    address_zip: string | null;
    address_combined: string | null;
  } | null;
  assignee: { full_name: string | null } | null;
}

export function appointmentLeadName(a: AppointmentRow): string {
  return (
    a.lead?.full_name ??
    [a.lead?.first_name, a.lead?.last_name].filter(Boolean).join(" ")
  ) || "Unknown lead";
}

export function appointmentAddress(a: AppointmentRow): string {
  return (
    [a.lead?.address_street, a.lead?.address_city, a.lead?.address_state, a.lead?.address_zip]
      .filter(Boolean)
      .join(", ") || a.lead?.address_combined || ""
  );
}

/** Book an appointment at the door — completes the stop atomically. */
export async function setStopAppointment(
  stopId: string,
  scheduledAt: string,
  notes?: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("set_appointment", {
    p_stop_id: stopId,
    p_scheduled_at: scheduledAt,
    p_notes: notes ?? null,
  });
  if (error) throw error;
  const appointmentId = data as string;
  void recordActivity({
    kind: "appointment_set",
    target_type: "appointment",
    target_id: appointmentId,
    summary: `Appointment booked for ${new Date(scheduledAt).toLocaleString()}`,
    metadata: { route_stop_id: stopId, scheduled_at: scheduledAt },
  });
  return appointmentId;
}

/**
 * Open appointments, oldest first — overdue ones surface at the top instead
 * of silently aging out. RLS scopes rows: admins see the team, agents see
 * their own.
 */
export async function listOpenAppointments(limit = 100): Promise<AppointmentRow[]> {
  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id, lead_id, route_stop_id, assigned_to, scheduled_at, status, notes, created_at, lead:leads(id, full_name, first_name, last_name, phone, address_street, address_city, address_state, address_zip, address_combined), assignee:app_users!appointments_assigned_to_fkey(full_name)",
    )
    .eq("status", "scheduled")
    .order("scheduled_at", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as AppointmentRow[];
}

export async function updateAppointmentStatus(
  id: string,
  status: Exclude<AppointmentStatus, "scheduled">,
): Promise<void> {
  const { error } = await supabase
    .from("appointments")
    .update({ status })
    .eq("id", id);
  if (error) throw error;
  void recordActivity({
    kind: "appointment_status_change",
    target_type: "appointment",
    target_id: id,
    summary: `Appointment marked ${status}`,
    metadata: { status },
  });
}
